import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Grid,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  VideoLibrary as VideoIcon,
  TextFields as TextIcon,
  Code as CodeIcon,
  Slideshow as PresentationIcon,
  Games as GameIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const BlockSelectionEditor = ({ 
  lesson, 
  originalLesson, 
  selectedBlocks, 
  onBlockSelectionChange,
  onChangesDetected 
}) => {
  const { t } = useTranslation(['lessons']);
  const [blockChanges, setBlockChanges] = useState({});

  // Функція для отримання іконки типу блоку
  const getBlockIcon = (type) => {
    switch (type) {
      case 'video':
        return <VideoIcon />;
      case 'presentation':
        return <PresentationIcon />;
      case 'fullHtml':
      case "htmlBlock":
        return <CodeIcon />;
      case 'flippingCards':
      case 'labyrinth':
      case 'memoryGame':
      case 'tagCloud':
      case 'rolePlayGame':
        return <GameIcon />;
      default:
        return <TextIcon />;
    }
  };

  // Функція для отримання назви типу блоку
  const getBlockTypeName = (type) => {
    return t(`lessons:contentTypes.${type}.name`) || type;
  };

  // Функція для перевірки чи є зміни в блоці
  const hasBlockChanges = (block, originalBlock) => {
    if (!originalBlock) return false; // Якщо немає оригінального блоку, значить це новий блок
    
    // Для основного контенту порівнюємо тільки content та contentType
    if (block.id === 'main_content') {
      return block.content !== originalBlock.content || 
             block.type !== originalBlock.type;
    }
    
    // Для контент-блоків порівнюємо основні поля (ігноруємо title, який додається автоматично)
    const currentContent = block.content || '';
    const originalContent = originalBlock.content || '';
    
    const currentType = block.type || '';
    const originalType = originalBlock.type || '';
    
    const currentDescription = block.description || '';
    const originalDescription = originalBlock.description || '';
    
    return currentContent !== originalContent ||
           currentType !== originalType ||
           currentDescription !== originalDescription;
  };

  // Функція для отримання всіх блоків уроку
  const getAllBlocks = () => {
    const blocks = [];
    
    // Основний контент (якщо є)
    if (lesson.content && lesson.contentType && lesson.contentType !== 'mixed') {
      blocks.push({
        id: 'main_content',
        type: lesson.contentType,
        content: lesson.content,
        title: t('lessons:blockSelection.mainContent'),
        isMainContent: true
      });
    }
    
    // Контент-блоки
    if (lesson.contentBlocks && lesson.contentBlocks.length > 0) {
      lesson.contentBlocks.forEach((block, index) => {
        blocks.push({
          id: `contentBlock_${index}`,
          ...block,
          title: `${t('lessons:blockSelection.block')} ${index + 1}`, // Автоматично додається для відображення
          isMainContent: false
        });
      });
    }
    
    return blocks;
  };

  // Функція для отримання оригінальних блоків
  const getOriginalBlocks = () => {
    const blocks = {};
    
    // Основний контент
    if (originalLesson.content && originalLesson.contentType && originalLesson.contentType !== 'mixed') {
      blocks['main_content'] = {
        type: originalLesson.contentType,
        content: originalLesson.content
      };
    }
    
    // Контент-блоки
    if (originalLesson.contentBlocks && originalLesson.contentBlocks.length > 0) {
      originalLesson.contentBlocks.forEach((block, index) => {
        blocks[`contentBlock_${index}`] = block;
      });
    }
    
    return blocks;
  };

  // Ефект для автоматичного виявлення та вибору змінених блоків
  useEffect(() => {
    const allBlocks = getAllBlocks();
    const originalBlocks = getOriginalBlocks();
    const changes = {};
    
    allBlocks.forEach(block => {
      const originalBlock = originalBlocks[block.id];
      const hasChanges = hasBlockChanges(block, originalBlock);
      changes[block.id] = hasChanges;
    });
    
    setBlockChanges(changes);
    
    // Автоматично вибираємо тільки змінені блоки
    const hasAnyChanges = Object.values(changes).some(hasChanges => hasChanges);
    if (hasAnyChanges) {
      const changedBlockIds = allBlocks
        .filter(block => changes[block.id])
        .map(block => block.id);
      
      // Автоматично встановлюємо вибір тільки змінених блоків
      onBlockSelectionChange(changedBlockIds);
    } else {
      // Якщо немає змін, очищаємо вибір
      onBlockSelectionChange([]);
    }
    
    // Повідомляємо батьківський компонент про зміни
    if (onChangesDetected) {
      onChangesDetected(hasAnyChanges);
    }
  }, [lesson, originalLesson]);

  const allBlocks = getAllBlocks();
  const hasAnyChanges = Object.values(blockChanges).some(hasChanges => hasChanges);

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">
          {t('lessons:blockSelection.title')}
        </Typography>
      </Box>

      {!hasAnyChanges && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: 'warning.light' }}>
          <Typography variant="body2" color="warning.dark">
            {t('lessons:blockSelection.noChangesDetected')}
          </Typography>
        </Paper>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('lessons:blockSelection.description')}
      </Typography>

      {/* Показуємо інформацію про автоматичний вибір */}
      {hasAnyChanges && selectedBlocks.length > 0 && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: 'info.light' }}>
          <Typography variant="body2" color="info.dark">
            {t('lessons:blockSelection.autoSelectionInfo', { 
              count: selectedBlocks.length 
            })}
          </Typography>
        </Paper>
      )}

      {allBlocks.map((block) => {
        const hasChanges = blockChanges[block.id];
        const isSelected = selectedBlocks.includes(block.id);
        
        return (
          <Paper key={block.id} sx={{ mb: 2 }}>
            <Accordion defaultExpanded={hasChanges}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                    {getBlockIcon(block.type)}
                    <Typography variant="subtitle1" sx={{ ml: 1, opacity: hasChanges ? 1 : 0.6 }}>
                      {block.title}
                    </Typography>
                    <Chip 
                      label={getBlockTypeName(block.type)} 
                      size="small" 
                      sx={{ ml: 1, opacity: hasChanges ? 1 : 0.6 }}
                    />
                    {hasChanges && (
                      <Chip 
                        label={t('lessons:blockSelection.modified')} 
                        size="small" 
                        color="warning"
                        sx={{ ml: 1 }}
                      />
                    )}
                    {!hasChanges && (
                      <Chip 
                        label={t('lessons:blockSelection.noChanges')} 
                        size="small" 
                        color="default"
                        sx={{ ml: 1 }}
                      />
                    )}
                  </Box>
                </Box>
              </AccordionSummary>
              
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom>
                      {t('lessons:blockSelection.originalContent')}
                    </Typography>
                    <Paper 
                      variant="outlined" 
                      sx={{ 
                        p: 2, 
                        bgcolor: 'background.default',
                        maxHeight: '200px',
                        overflow: 'auto',
                        fontFamily: 'monospace',
                        fontSize: '0.9rem'
                      }}
                    >
                      {(() => {
                        const originalBlocks = getOriginalBlocks();
                        const originalBlock = originalBlocks[block.id];
                        if (!originalBlock) {
                          return t('lessons:blockSelection.noOriginalContent');
                        }
                        return originalBlock.content || t('lessons:blockSelection.emptyContent');
                      })()}
                    </Paper>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom>
                      {t('lessons:blockSelection.currentContent')}
                    </Typography>
                    <Paper 
                      variant="outlined" 
                      sx={{ 
                        p: 2, 
                        bgcolor: 'background.default',
                        maxHeight: '200px',
                        overflow: 'auto',
                        fontFamily: 'monospace',
                        fontSize: '0.9rem',
                        borderColor: hasChanges ? 'warning.main' : 'divider'
                      }}
                    >
                      {block.content || t('lessons:blockSelection.emptyContent')}
                    </Paper>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Paper>
        );
      })}

      {allBlocks.length === 0 && (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            {t('lessons:blockSelection.noBlocks')}
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default BlockSelectionEditor;
